import { useEffect } from "react";
import { Outlet, useMatch, useNavigate } from "react-router";
import { matchesAccelerator, PAGE_ACCELERATORS } from "@ide/shared";
import { useLogout } from "@/components/app-shell";
import { useChatUnread, useMe } from "@/lib/queries";
import { EDITOR_FONT, useTextSize } from "@/lib/text-size";
import { backOnePage, desktop, forwardOnePage, isMac } from "./bridge";
import { isAvailable, runCommand, useCommands } from "./commands";
import { toast } from "sonner";
import { DesktopDialogs } from "./dialogs";
import { FULL_EDITION, hasFeature } from "./edition";
import { openLocalFolderInEditor } from "./local-folder";
import { ImportRepoDialog } from "./import-repo";
import { ImportVsCodeDialog, VsCodeOffer } from "./import-vscode";
import { LoginDialog } from "./login-dialog";
import { GitHubConnectDialog } from "./github-connect";
import { QuickOpen } from "./quick-open";
import { DesktopTitleBar } from "./title-bar";
import { useThemePicker } from "./theme-picker";
import { OnboardingTour, useTour } from "@/components/onboarding-tour";
import { refreshApp, reloadWindow } from "@/lib/refresh";
import { useDesktopUi, type AiProvider } from "./ui";
import { ChatSidebarOutside } from "@/workspace/chat-sidebar";
import { RightPanelFrame, useRightTabs } from "@/workspace/right-panel";
import { useSyncAiProviders } from "@/plugins/ai-providers";
import { useColoredIcons } from "@/plugins/use-plugins";
import { CodeEngineDialog, useFirstRunEnginePrompt } from "@/workspace/code-engine";
import { ChatHeads } from "@/chat/chat-heads";
import { ClaudeConnectDialog } from "@/plugins/claude-connect";
import { toggleChat, useOpenChat } from "./chat";
import { initLocalRuns } from "@/runs/local-runs";
import { RunLocalDialog } from "@/runs/run-dialog";
import { RunLogDialog } from "@/runs/runs-ui";

/** Comandos que valem em qualquer página (o projeto aberto registra os dele por cima). */
function useGlobalCommands() {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const logout = useLogout();
  const ui = useDesktopUi((s) => s.set);
  const full = FULL_EDITION;
  useCommands({
    "quick-open": () => ui({ quickOpen: "all" }),
    "global-search": () => ui({ quickOpen: "all" }),
    "command-palette": () => ui({ quickOpen: "all" }),
    "open-project": () => ui({ quickOpen: "projects" }),
    "open-folder": () => void openLocalFolderInEditor(),
    "new-project": () => navigate(`/projects?novo=${Date.now()}`),
    "go-projects": () => navigate("/projects"),
    "go-chat": () => navigate("/chat"),
    "go-back": backOnePage,
    "go-forward": forwardOnePage,
    refresh: () => void refreshApp(),
    ...(me && !me.user.isGuest ? { "show-tour": () => useTour.getState().show() } : {}),
    "reload-window": reloadWindow,
    shortcuts: () => ui({ shortcuts: true }),
    "editor-font-bigger": () => useTextSize.getState().setEditorFont(useTextSize.getState().editorFont + 1),
    "editor-font-smaller": () => useTextSize.getState().setEditorFont(useTextSize.getState().editorFont - 1),
    "editor-font-reset": () => useTextSize.getState().setEditorFont(EDITOR_FONT.default),
    ...(me ? { logout: () => void logout() } : {}),
    ...(full
      ? {
          settings: () => navigate("/settings"),
          // Troca rápida (barra de título); a loja fica a um clique, no fim do menu.
          "color-themes": () => useThemePicker.getState().setOpen(true),
          "go-discover": () => navigate("/discover"),
          "go-groups": () => navigate("/groups"),
          "go-plugins": () => navigate("/plugins"),
          // Fora de um projeto, Extensões abre a loja (no projeto, o painel lateral).
          "panel-extensions": () => navigate("/plugins"),
          ...(me ? { "go-profile": () => navigate(`/u/${me.user.handle}`) } : {}),
        }
      : {}),
    ...(hasFeature("ai") ? { "toggle-chat": () => toggleChat() } : {}),
    ...(desktop ? { "import-vscode": () => ui({ importVsCode: true }) } : {}),
    // Versões antigas do app não sabem trocar o ícone: o item fica desabilitado nelas.
    ...(desktop?.appIcon ? { "app-icon": () => navigate("/settings?aba=appearance") } : {}),
    ...(desktop
      ? { "change-server": () => ui({ server: true }), "import-repo": () => ui({ importRepo: true }) }
      : {}),
  });
}

/**
 * Atalhos que a página trata (Ctrl+W, Ctrl+Tab, Ctrl+\, Ctrl+G, Ctrl+K): fora do terminal, viram o
 * comando do menu; dentro dele, a tecla vai para o shell (apagar palavra, cancelar, SIGQUIT…).
 */
function usePageAccelerators() {
  useEffect(() => {
    if (!desktop) return;
    const onKey = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      if ((e.target as HTMLElement | null)?.closest?.(".xterm")) return;
      const hit = PAGE_ACCELERATORS.find((a) => matchesAccelerator(a.accelerator, e, isMac));
      if (!hit || !isAvailable(hit.command)) return;
      e.preventDefault();
      e.stopPropagation();
      runCommand(hit.command);
    };
    // Captura: vem antes do editor (o Ctrl+G do CodeMirror é "próxima ocorrência"; aqui é "ir para linha").
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);
}

/** Barra de título nativa nas cores do tema (claro/escuro e temas de cor, como o Hacker). */
function useWindowColors() {
  useEffect(() => {
    if (!desktop) return;
    const root = document.documentElement;
    let last = "";
    const hex = (v: string) => (/^#[0-9a-f]{6}$/.test(v) ? v : null);
    const sync = () => {
      const css = getComputedStyle(root);
      const read = (name: string) => hex(css.getPropertyValue(name).trim().toLowerCase());
      const background = read("--canvas");
      const foreground = read("--fg");
      if (!background || !foreground) return;
      // As vistas do motor VS Code (Claude, ferramentas das extensões) se pintam com as mesmas cores.
      const ui = {
        surface: read("--surface"),
        panel: read("--panel"),
        muted: read("--bg-muted"),
        emphasis: read("--bg-emphasis"),
        border: read("--border"),
        fgMuted: read("--fg-muted"),
        accent: read("--accent"),
        accentFg: read("--accent-fg"),
      };
      const complete = Object.values(ui).every(Boolean) ? (ui as Record<keyof typeof ui, string>) : undefined;
      const vscodeTheme = root.dataset.vscodeTheme || null;
      const dark = root.classList.contains("dark");
      const key = JSON.stringify([background, foreground, dark, complete, vscodeTheme]);
      if (key === last) return;
      last = key;
      desktop?.setTheme({ background, foreground, dark, ...(complete ? { ui: complete } : {}), vscodeTheme });
    };
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ["class", "style", "data-vscode-theme"] });
    return () => observer.disconnect();
  }, []);
}

/** Lateral direita fora de um projeto: só as abas dos chats de IA instalados. */
function OutsideChat({ provider }: { provider: AiProvider }) {
  const tabs = useRightTabs({ inProject: false });
  return (
    <RightPanelFrame tabs={tabs} active={provider}>
      <ChatSidebarOutside provider={provider} />
    </RightPanelFrame>
  );
}

/** Mensagens não lidas no ícone do app (Dock, barra de tarefas). */
function UnreadBadge() {
  const { data: me } = useMe();
  const { data: unread = 0 } = useChatUnread(Boolean(me));
  useEffect(() => desktop?.setUnread(me ? unread : 0), [me, unread]);
  return null;
}

/**
 * Raiz de todas as rotas. No app desktop: barra de título no estilo do VS Code em cima e o app
 * embaixo, com rolagem própria. Na web: só o app (e a busca rápida com Ctrl/⌘+K).
 */
export function RootLayout() {
  const navigate = useNavigate();
  const inProject = Boolean(useMatch("/p/:projectId"));
  const { data: me } = useMe();
  // Ofertas da primeira abertura só depois de entrar e responder as boas-vindas.
  const ready = Boolean(me && !me.user.isGuest && me.user.onboarded !== false);
  useSyncAiProviders();
  // Ícones de arquivo fora do projeto (chat, anexos) seguem a extensão de ícones.
  useColoredIcons();
  // O motor VS Code já vem instalado: na primeira abertura, pergunta se traz as extensões.
  useFirstRunEnginePrompt(Boolean(desktop) && ready);
  const chat = useOpenChat();
  useGlobalCommands();
  usePageAccelerators();
  useWindowColors();
  useEffect(() => desktop?.onCommand(runCommand), []);
  useEffect(() => initLocalRuns(), []);
  // Convidado tentou algo que exige conta: no app, o diálogo "Entrar"; na web, o aviso.
  useEffect(() => {
    const onNeed = (e: Event) => {
      const message = (e as CustomEvent<string>).detail;
      if (desktop) useDesktopUi.getState().set({ login: message });
      else toast(message, { action: { label: "Entrar", onClick: () => navigate("/login") } });
    };
    window.addEventListener("kompil:account-required", onNeed);
    return () => window.removeEventListener("kompil:account-required", onNeed);
  }, [navigate]);
  useEffect(() => {
    const go = (e: Event) => navigate((e as CustomEvent<string>).detail);
    window.addEventListener("kompil:navigate", go);
    return () => window.removeEventListener("kompil:navigate", go);
  }, [navigate]);
  // Web: Ctrl/⌘+K abre a busca rápida (Ctrl+P é imprimir no navegador). No app, os menus cuidam.
  useEffect(() => {
    if (desktop) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k" && !e.shiftKey) {
        e.preventDefault();
        runCommand("quick-open");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const extras = (
    <>
      <QuickOpen />
      <OnboardingTour />
      <ChatHeads />
      <DesktopDialogs />
      {desktop ? <LoginDialog /> : null}
      {desktop ? <GitHubConnectDialog /> : null}
      {desktop ? <ClaudeConnectDialog /> : null}
      {desktop ? <ImportRepoDialog /> : null}
      {desktop ? <ImportVsCodeDialog /> : null}
      {desktop ? <CodeEngineDialog /> : null}
      {desktop && ready ? <VsCodeOffer /> : null}
      {desktop ? <RunLocalDialog /> : null}
      {desktop ? <RunLogDialog /> : null}
    </>
  );
  if (!desktop) {
    return (
      <>
        <Outlet />
        {extras}
      </>
    );
  }
  return (
    <div className="flex h-full flex-col bg-bg">
      <DesktopTitleBar />
      <div className="flex min-h-0 flex-1">
        <div className="relative min-w-0 flex-1">
          <div className="absolute inset-0 overflow-auto">
            <Outlet />
          </div>
        </div>
        {/* Dentro de um projeto, o chat é um painel do próprio projeto (redimensionável). */}
        {chat && !inProject ? (
          <aside className="w-[360px] shrink-0 border-l border-border" aria-label="Chat com a IA">
            <OutsideChat provider={chat} />
          </aside>
        ) : null}
      </div>
      <UnreadBadge />
      {extras}
    </div>
  );
}
